// Router configuration with public and protected routes
import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { getDashboardRoute, getDefaultGuestSignInPath, isOwnerRole, isStudentRole } from '@utils/routing';
import { Spin } from 'antd';
import Inside from '@layout/Inside';
import Outside from '@layout/Outside';
import Signin from '@/pages/outside/Signin';
import Signup from '@/pages/outside/Signup';
import ForgotPassword from '@/pages/outside/ForgotPassword';
import Otp from '@/pages/outside/Otp';
import ResetPassword from '@/pages/outside/ResetPassword';
import NotFound from '@/pages/outside/NotFound';
import NotAuthorized from '@/pages/outside/NotAuthorized';
import RequireAuth from '@routes/guards/RequireAuth';
import RequireAbility from '@routes/guards/RequireAbility';
import TeacherAttendanceKiosk from '@/pages/inside/tenant/TeacherAttendanceKiosk';
import RootErrorFallback from '@/components/common/RootErrorFallback';
import { useAuthStore } from '@stores/auth.store';
import { getRoutesByRole } from '@routes/getRoutes';

import { Spinner } from '@/pages/style';

const GuestSignInRedirect = () => <Navigate to={getDefaultGuestSignInPath()} replace />;

const SuspenseWrapper = ({ children }) => (
    <Suspense fallback={<Spinner><Spin size="large" /></Spinner>}>
        {children}
    </Suspense>
);

const ConditionalLayout = ({ children }) => {
    const user = useAuthStore((state) => state.user);
    const isRefreshing = useAuthStore((state) => state.isRefreshing);

    if (isRefreshing) {
        return <Spinner><Spin size="large" /></Spinner>;
    }

    return user ? <Inside>{children}</Inside> : <Outside>{children}</Outside>;
};

// Dynamic route component that loads the correct component based on role
const DynamicRoute = ({ route }) => {
    const role = useAuthStore((state) => state.role);
    const user = useAuthStore((state) => state.user);
    const userRole = role || user?.role;

    // Get the component for this route based on role
    const Component = route.component;

    // Wrap with RequireAbility if needed
    if (route.requireAbility) {
        return (
            <RequireAbility can={route.requireAbility}>
                <Component />
            </RequireAbility>
        );
    }

    return <Component />;
};

// Root redirect — signed-in users to role dashboard; guests to the right sign-in
const RootRedirect = () => {
    const isRefreshing = useAuthStore((state) => state.isRefreshing);
    const user = useAuthStore((state) => state.user);
    const role = useAuthStore((state) => state.role);

    if (isRefreshing) {
        return <Spinner><Spin size="large" /></Spinner>;
    }

    if (user) {
        const userRole = role || user?.role;
        return <Navigate to={getDashboardRoute(userRole)} replace />;
    }

    return <Navigate to={getDefaultGuestSignInPath()} replace />;
};

// Build dynamic routes based on user role
const buildDynamicRoutes = () => {
    // Get routes to find dashboard component
    const ownerRoutes = getRoutesByRole('owner');
    const tenantRoutes = getRoutesByRole('tenant');
    const studentRoutes = getRoutesByRole('student');
    const ownerDashboardRoute = ownerRoutes.find(r => r.path === '/dashboard');
    const tenantDashboardRoute = tenantRoutes.find(r => r.path === '/dashboard');
    const studentDashboardRoute = studentRoutes.find(r => r.path === '/student/dashboard');
    const tenantDashboardRouteFull = tenantRoutes.find(r => r.path === '/tenant/dashboard');

    // Get tenants route
    const ownerTenantsRoute = ownerRoutes.find(r => r.path === '/tenants');
    const tenantTenantsRoute = tenantRoutes.find(r => r.path === '/tenants');

    const publicRoutes = [
        {
            path: '/',
            element: <RootRedirect />
        },
        {
            path: '/dashboard',
            element: (
                <RequireAuth>
                    <Inside>
                        <SuspenseWrapper>
                            <RouteResolver
                                path="/dashboard"
                                ownerRoute={ownerDashboardRoute}
                                tenantRoute={tenantDashboardRoute}
                                studentRoute={studentDashboardRoute}
                            />
                        </SuspenseWrapper>
                    </Inside>
                </RequireAuth>
            )
        },
        {
            path: '/tenant/dashboard',
            element: (
                <RequireAuth>
                    <Inside>
                        <SuspenseWrapper>
                            {tenantDashboardRouteFull ? (
                                (() => {
                                    const Component = tenantDashboardRouteFull.component;
                                    return <Component />;
                                })()
                            ) : (
                                <NotFound />
                            )}
                        </SuspenseWrapper>
                    </Inside>
                </RequireAuth>
            )
        },
        {
            path: '/student/dashboard',
            element: (
                <RequireAuth>
                    <Inside>
                        <SuspenseWrapper>
                            {studentDashboardRoute ? (
                                (() => {
                                    const Component = studentDashboardRoute.component;
                                    return <Component />;
                                })()
                            ) : (
                                <NotFound />
                            )}
                        </SuspenseWrapper>
                    </Inside>
                </RequireAuth>
            )
        },
        {
            path: '/tenants',
            element: (
                <RequireAuth>
                    <Inside>
                        <SuspenseWrapper>
                            <RouteResolver
                                path="/tenants"
                                ownerRoute={ownerTenantsRoute}
                                tenantRoute={tenantTenantsRoute}
                            />
                        </SuspenseWrapper>
                    </Inside>
                </RequireAuth>
            )
        },
        {
            path: '/public/attendance',
            element: (
                <SuspenseWrapper>
                    <TeacherAttendanceKiosk />
                </SuspenseWrapper>
            )
        },
        {
            path: '/sign-in',
            element: <GuestSignInRedirect />
        },
        {
            path: '/owner/sign-in',
            element: (
                <Outside>
                    <Signin portal="owner" />
                </Outside>
            )
        },
        {
            path: '/tenant/sign-in',
            element: (
                <Outside>
                    <Signin portal="tenant" />
                </Outside>
            )
        },
        {
            path: '/sign-up',
            element: (
                <Outside>
                    <Signup />
                </Outside>
            )
        },
        {
            path: '/forgot-password',
            element: (
                <Outside>
                    <ForgotPassword />
                </Outside>
            )
        },
        {
            path: '/otp',
            element: (
                <Outside>
                    <Otp />
                </Outside>
            )
        },
        {
            path: '/reset-password',
            element: (
                <Outside>
                    <ResetPassword />
                </Outside>
            )
        },
        {
            path: '/not-authorized',
            element: (
                <ConditionalLayout>
                    <NotAuthorized />
                </ConditionalLayout>
            )
        },
        {
            path: '*',
            element: (
                <ConditionalLayout>
                    <NotFound />
                </ConditionalLayout>
            )
        }
    ];

    // Combine unique paths from all roles
    const allPaths = new Set([
        ...ownerRoutes.map(r => r.path),
        ...tenantRoutes.map(r => r.path),
        ...studentRoutes.map(r => r.path)
    ]);

    const dynamicRoutes = Array.from(allPaths)
        .filter(path => path !== '/dashboard' && path !== '/tenant/dashboard' && path !== '/student/dashboard' && path !== '/tenants') // Exclude public routes already declared above
        .map(path => {
            const ownerRoute = ownerRoutes.find(r => r.path === path);
            const tenantRoute = tenantRoutes.find(r => r.path === path);
            const studentRoute = studentRoutes.find(r => r.path === path);

            return {
                path,
                element: (
                    <RequireAuth>
                        <Inside>
                            <SuspenseWrapper>
                                <RouteResolver
                                    path={path}
                                    ownerRoute={ownerRoute}
                                    tenantRoute={tenantRoute}
                                    studentRoute={studentRoute}
                                />
                            </SuspenseWrapper>
                        </Inside>
                    </RequireAuth>
                )
            };
        });

    return [...publicRoutes, ...dynamicRoutes].map((route) => ({
        ...route,
        errorElement: <RootErrorFallback />
    }));
};

// Component that resolves which route component to render based on user role
const RouteResolver = ({ path, ownerRoute, tenantRoute, studentRoute }) => {
    const role = useAuthStore((state) => state.role);
    const user = useAuthStore((state) => state.user);
    const userRole = role || user?.role;

    const isOwner = userRole ? isOwnerRole(userRole) : false;
    const isStudent = userRole ? isStudentRole(userRole) : false;
    const route = isOwner ? ownerRoute : (isStudent ? studentRoute : tenantRoute);

    if (!route) {
        return <NotFound />;
    }

    const Component = route.component;

    if (route.requireAbility) {
        return (
            <RequireAbility can={route.requireAbility}>
                <Component />
            </RequireAbility>
        );
    }

    return <Component />;
};

const router = createBrowserRouter(buildDynamicRoutes());

const RoutesRoot = () => {
    return <RouterProvider router={router} />;
};

export default RoutesRoot;
